import {EventEmitter, Injectable} from '@angular/core';
import {ApplicationState} from '../models/application-state';
import {Offer} from '../../core/models/offer/offer.model';
import {ApplicationStateStorageService} from './application-state-storage.service';
import {QueryParamsModel} from '../models/query-params.model';
import {CalculationState} from '../../core/models/calculation-state';
import {BusinessParameter, RequestHeader} from '../models/request-offer/request-header.model';
import {BusinessParamName} from '../models/request-offer/request-offer.enum';
import {OffersState} from '../models/offers-state.model';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {InsuranceCompanyCode} from '../models/insurance-company-code.enum';
import {StepConfig} from '../../core/configuration/step-config';
import {BehaviorSubject} from 'rxjs/Rx';
import {AppMode} from '../models/app-mode.enum';
import {RegistrationState} from '../models/registration-state';
import {OcDamagesDataService} from './oc-damages-data.service';
import {ObjectUtils} from '../../core/utils/object.utils';
import * as _ from 'lodash';
import {CryptoService} from './crypto.service';
import {OfferModification} from '../../core/models/offer/offer-modification.model';
import {OfferPrimaryCoverageCode} from '../../core/consts/offer-primary-coverage-code.enum';
import {QueryParamName} from '../../core/enums/query-param-name.enum';
import {CoOwnerModel} from '../../core/models/owner-data/co-owner.model';
import {OfferResponseStatusCode} from '../../core/enums/offer/offer-response-status-code.enum';

@Injectable({
  providedIn: 'root'
})
export class ApplicationStateService {

  initialState: any;
  stateExistingInStorage: boolean;
  state: ApplicationState;
  firstEntry: boolean; // defines whether it's first entry to the application (true) or after refresh (false)
  orderPolicySubmitted: boolean = false;
  sessionDataLoaded: boolean = false; // session came and include data
  aztecDecoderLibInitialized: boolean = false;
  aztecScannerActive: boolean = false;
  previewSectionMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  appModeChange: EventEmitter<AppMode> = new EventEmitter<AppMode>();
  sessionId: string;
  dataLoadedFromCalculationsCache: boolean; // resets after refresh

  constructor(private storage: ApplicationStateStorageService,
              private ocDamagesDataService: OcDamagesDataService,
              private cryptoService: CryptoService) {
    this.createOrSetApplicationState();
  }

  get selectedOffer(): Offer {
    return this.state.selectedOffer;
  }

  get selectedOfferNotModified(): Offer {
    return this.state.selectedOfferNotModified;
  }

  set selectedOffer(offer: Offer) {
    this.state.selectedOffer = offer;
    this.state.selectedOfferNotModified = _.cloneDeep(offer);
    this.separateInsurer = false;
    this.storage.updateApplicationState();
  }

  set selectedOfferStatusCode(code: OfferResponseStatusCode) {
    this.state.selectedOfferStatusCode = code;
    this.storage.updateApplicationState();
  }

  get offersState(): OffersState {
    return this.state.offersState;
  }

  get salesChannel(): string {
    return this.state.salesChannel;
  }

  set salesChannel(salesChannel: string) {
    this.state.salesChannel = salesChannel;
    this.storage.updateApplicationState();
  }

  get processId(): string {
    if (this.state.queryParams && this.state.queryParams[QueryParamName.PROCESS_ID]) {
      return this.state.queryParams[QueryParamName.PROCESS_ID];
    }
    return this.state ? this.state.processContext.processId : null;
  }

  set processId(id: string) {
    if (isNotNullOrUndefined(this.state)) {
      this.state.processContext.processId = id;
    }
  }

  get mode(): AppMode {
    return this.state.mode;
  }

  set mode(mode: AppMode) {
    this.state.mode = mode;
    this.appModeChange.emit(mode);
  }

  get dirtyFieldsBean() {
    return this.state.dirtyFields;
  }

  set dirtyFieldsBean(salesChannel: any) {
    this.state.dirtyFields = {...this.state.dirtyFields, ...salesChannel};
    this.storage.updateApplicationState();
  }

  get isWaitingForOfferMode(): boolean {
    return this.state.mode === AppMode.WAITING_FOR_OFFER || this.state.mode === AppMode.WAITING_FOR_OFFER_LOADING;
  }

  get selectedOfferPackageName(): string {
    return this.selectedOffer ? this.selectedOffer.packageName : null;
  }

  get offerPackageName(): string {
    return this.state.offerPackageName;
  }

  set offerPackageName(packageName: string) {
    this.state.offerPackageName = packageName;
    this.storage.updateApplicationState();
  }

  get insuranceCompanyCode(): InsuranceCompanyCode {
    return <InsuranceCompanyCode>this.getBusinessParameterValue(BusinessParamName.INSURANCE_COMPANY_CODE);
  }

  get offerPackageNameBusinessParam(): string {
    return <string>this.getBusinessParameterValue(BusinessParamName.OFFER_PACKAGE_NAME);
  }

  get isLoggedUserIdBusinessParam(): boolean {
    return isNotNullOrUndefined(this.getBusinessParameterValue(BusinessParamName.LOGGED_USER_ID));
  }

  set cessionPostalCode(postalCode: string) {
    this.state.cessionPostalCode = postalCode;
    this.storage.updateApplicationState();
  }

  get cessionPostalCode(): string {
    return this.state.cessionPostalCode;
  }

  get registrationState(): RegistrationState {
    return this.state.registrationState;
  }

  set registrationState(state: RegistrationState) {
    this.state.registrationState = state;
  }

  set dataFromSessionIncluded(value: boolean) {
    this.registrationState.dataFromSessionIncluded = value;
    this.storage.updateApplicationState();
  }

  set bankierIdSalesChannelBankier(id: string) {
    this.state.bankierIdSalesChannelBankier = id;
    this.storage.updateApplicationState();
  }

  get analyticsUserId(): string {
    return <string>this.getBusinessParameterValue(BusinessParamName.ANALYTICS_USER_ID);
  }

  set selectedOfferOriginalModifications(modifications: OfferModification[]) {
    this.state.selectedOfferOriginalModifications = _.cloneDeep(modifications);
    this.storage.updateApplicationState();
  }

  get selectedOfferOriginalModifications(): OfferModification[] {
    return this.state.selectedOfferOriginalModifications;
  }

  set separateInsurer(separateInsurer: boolean) {
    this.state.separateInsurer = separateInsurer;
    this.storage.updateApplicationState();
  }

  get separateInsurer(): boolean {
    return this.state.separateInsurer;
  }

  set entryUrl(url: string) {
    if (!isNotNullOrUndefined(this.state.entryUrl)) {
      this.state.entryUrl = url;
      this.storage.updateApplicationState();
    }
  }

  get entryUrl(): string {
    return this.state.entryUrl;
  }

  set redirectedToPayment(value: boolean) {
    this.state.redirectedToPayment = value;
    this.storage.updateApplicationState();
  }

  get redirectedToPayment(): boolean {
    return this.state.redirectedToPayment;
  }

  set selectedPremiumCode(value: string) {
    this.state.selectedPremiumCode = value;
    this.storage.updateApplicationState();
  }

  get selectedPremiumCode(): string {
    return this.state.selectedPremiumCode;
  }

  get isSessionStageRecreate(): boolean {
    return this.getBusinessParameterValue(BusinessParamName.SESSION_STAGE) === 'RECREATE';
  }

  get partnerBusinessParamsDefined(): boolean {
    return isNotNullOrUndefined(this.getBusinessParameterValue(BusinessParamName.PARTNER_SHORT_NAME)) || isNotNullOrUndefined(this.getBusinessParameterValue(BusinessParamName.PORTAL_PARTNER_ID));
  }

  getBusinessParameterValue(paramName: BusinessParamName): string | string[] | boolean {
    const parameter = this.state.processContext.businessParameters.find(param => paramName === param.parameterName);
    return parameter ? parameter.parameterValue : null;
  }

  getAllBusinessParameters(): BusinessParameter[] {
    return this.state.processContext.businessParameters;
  }

  saveBusinessParameter(paramName: BusinessParamName, paramValue: string | string[] | boolean): void {
    const businessParameters = this.state.processContext.businessParameters.filter(param => paramName === param.parameterName);
    if (businessParameters && businessParameters[0]) {
      businessParameters[0].parameterValue = paramValue;
    } else {
      this.state.processContext.businessParameters.push({
        parameterName: paramName,
        parameterValue: paramValue
      });
    }
    this.storage.updateApplicationState();
  }

  // OFFERS STATE

  initOffersState(): void {
    this.offersState.offersCounter = 0;
    this.offersState.displayedOffersNumber = 0;
    this.offersState.offersTotalNumber = null;
    this.offersState.offersHeaders = [];
  }

  /**
   * saves needed business parameters from selected offer's header
   */
  saveNecessaryBusinessParamsFromOffer(id: string): void {
    const offerBusinessParams = this.offersHeaders
      .map(header => header.processContext.businessParameters) // array of business params
      .find(businessParams => {
        const offerId = businessParams.find(paramName => paramName.parameterName === BusinessParamName.OFFER_ID);
        return offerId ? offerId.parameterValue === id : false;
      });

    if (isNotNullOrUndefined(offerBusinessParams) && offerBusinessParams.length) {
      this.saveBusinessParamsInState(offerBusinessParams, [
        BusinessParamName.INSURANCE_COMPANY_CODE,
        BusinessParamName.CHANGED_OFFER,
        BusinessParamName.QUOTATION_ID,
      ]);
    }
  }

  getBusinessParamFromOffer(id: string, parameterName: string): any {
    const offerBusinessParams = this.offersHeaders
      .map(header => header.processContext.businessParameters) // array of business params
      .find(businessParams => {
        const offerId = businessParams.find(paramName => paramName.parameterName === BusinessParamName.OFFER_ID);
        return offerId ? offerId.parameterValue === id : false;
      });

    const parameter = offerBusinessParams ? offerBusinessParams.find(param => param.parameterName === parameterName) : null;
    return parameter ? parameter.parameterValue : null;
  }

  getTechnicalParamFromOffer(id: string, parameterName: string): any {
    const offerHeader = this.offersHeaders
      .find(header => {
        const offerId = header.processContext.businessParameters.find(paramName => paramName.parameterName === BusinessParamName.OFFER_ID);
        return offerId ? offerId.parameterValue === id : false;
      });

    if (isNotNullOrUndefined(offerHeader)) {
      const offerTechnicalParams = offerHeader.technicalParameters;
      const parameter = offerTechnicalParams.find(param => param.parameterName === parameterName);
      return parameter ? parameter.parameterValue : null;
    } else {
      return null;
    }
  }

  updateDirtyField(sectionKey: string, fieldKey: string, isDirty: boolean): void {
    const key = `${sectionKey}.${fieldKey}`;
    this.state.dirtyFields[key] = isDirty;
  }

  updateNavigationState(stepConfig: StepConfig): void {
    if (stepConfig.hasOwnProperty('substepIndex')) {
      this.state.navigationState.currentSubstepIndex = stepConfig.substepIndex;
    } else {
      this.state.navigationState.currentSubstepIndex = null;
    }
    this.storage.updateApplicationState();
  }

  updateApplicationState(): void {
    this.storage.updateApplicationState();
  }

  saveSelectedUserFilters(coverages: string[]): void {
    if (isNotNullOrUndefined(coverages) && coverages.length) {
      Object.keys(OfferPrimaryCoverageCode).forEach(coverage => {
        this.state.insuranceCoverages[coverage] = coverages.includes(coverage);
      });
    }
  }

  get allOffersReady(): boolean {
    return this.offersState.offersCounter === this.offersState.offersTotalNumber;
  }

  set offersTotalNumber(number: number) {
    this.offersState.offersTotalNumber = number;
  }

  get offersTotalNumber(): number {
    return this.offersState.offersTotalNumber;
  }

  get displayedOffersNumber(): number {
    return this.offersState.displayedOffersNumber;
  }

  get foundOffersPercent(): number {
    return (this.offersState.offersCounter / this.offersTotalNumber) * 100;
  }

  get offersHeaders(): RequestHeader[] {
    return this.offersState.offersHeaders;
  }

  addOfferHeader(header: RequestHeader): void {
    this.offersState.offersHeaders.push(header);
    this.ocDamagesDataService.processOfferHeader(header);
  }

  handleRegistrationDataFromAztec(data: any): void {
    if (data.hasOwnProperty('registrationData')) {
      this.state.dataFromAztec = true;
      this.state.usedPreviewMode = true;
      let registrationData;
      if (isNotNullOrUndefined(this.registrationState)) { // registration from session exists
        registrationData = ObjectUtils.mergeDeep(this.registrationState.registrationFromSession, data.registrationData);
      } else {
        registrationData = data.registrationData;
      }
      this.registrationState = {
        registrationFromSession: registrationData,
        dataFromSessionIncluded: false
      };
    }
  }

  createAnalyticsUserId(email: string): void {
    if (!isNotNullOrUndefined(this.analyticsUserId) && isNotNullOrUndefined(email) && !!email.length) {
      const hash = this.cryptoService.md5(email);
      this.saveBusinessParameter(BusinessParamName.ANALYTICS_USER_ID, hash);
    }
  }

  removeApplicationState() {
    this.storage.removeState();
  }

  updateCoOwnersState(coOwners: CoOwnerModel[]): void {
    this.state.calculationState.coOwnersState = coOwners.map(coOwner => ({
      noDrivingLicense: !isNotNullOrUndefined(coOwner.drivingLicenseIssueYear)
    }));
  }

  resetApplicationState(): void {
    this.state = new ApplicationState();
    this.stateExistingInStorage = false;
    this.storage.createApplicationState(this.state);
    this.initRawApplicationState();
  }

  resetEnginePower(): void {
    this.state.enginePower = null;
    this.updateApplicationState();
  }

  private createOrSetApplicationState(): void {
    this.state = new ApplicationState();

    if (!this.storage.isApplicationState()) {
      this.stateExistingInStorage = false;
      this.storage.createApplicationState(this.state);
      this.initRawApplicationState();
      this.firstEntry = true;
    } else {
      this.stateExistingInStorage = true;
      this.initApplicationStateFromStorage();
      this.storage.setApplicationState(this.state);
      this.firstEntry = false;
    }

    this.initialState = _.cloneDeep(this.state);
  }

  private initRawApplicationState(): void {
    this.state.selectedOffer = null;
    this.state.queryParams = new QueryParamsModel();
    this.state.calculationState = new CalculationState();
  }

  private initApplicationStateFromStorage(): void {
    this.state = this.storage.getApplicationState();
  }

  private saveBusinessParamsInState(params: BusinessParameter[], paramsToSave: BusinessParamName[]): void {
    paramsToSave.forEach(paramToSaveName => {
      const paramsWithName = params.filter(p => p.parameterName === paramToSaveName);
      const paramValue = (paramsWithName && paramsWithName.length) ? paramsWithName[paramsWithName.length - 1].parameterValue : null;
      this.saveBusinessParameter(paramToSaveName, paramValue);
    });
  }
}
